import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiResponseProjectsService } from '../../services/api/response-projects/api-response-projects.service';

type SaveStatus = 'idle' | 'saved' | 'confirmed';

@Component({
  selector: 'app-response-project-instructions',
  templateUrl: './response-project-instructions.component.html',
  styleUrls: ['./response-project-instructions.component.scss'],
})
export class ResponseProjectInstructionsComponent implements OnChanges, OnDestroy {
  @Input() projectId = '';
  @Input() instructions = '';
  @Output() projectNotFound = new EventEmitter<void>();
  @Output() projectClosed = new EventEmitter<void>();
  @Input() disabled = false;

  value = '';
  saveStatus: SaveStatus = 'idle';
  lastSaved: Date | null = null;
  relativeTimeKey = '';
  relativeTimeParams: Record<string, string | number> = {};
  showExample = false;
  focused = false;
  borderFlash = false;
  @Input() collapsed = false;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private confirmedTimer: ReturnType<typeof setTimeout> | null = null;
  private relativeTimeInterval: ReturnType<typeof setInterval> | null = null;

  constructor(private apiResponseProjects: ApiResponseProjectsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['instructions']) {
      this.value = this.instructions ?? '';
      this.saveStatus = 'idle';
      this.lastSaved = null;
      this.relativeTimeKey = '';
      this.relativeTimeParams = {};
      this.borderFlash = false;
      this.focused = false;
      this.clearTimers();
    }
    if (changes['disabled'] && this.disabled) {
      this.collapsed = true;
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  onInputChange(newValue: string): void {
    this.value = newValue;
    this.saveStatus = 'idle';

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.confirmedTimer) clearTimeout(this.confirmedTimer);

    this.debounceTimer = setTimeout(() => this.performSave(), 1500);
  }

  onFocus(): void {
    this.focused = true;
  }

  onBlur(): void {
    this.focused = false;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.performSave();
    }
  }

  get textareaBorderColor(): string {
    if (this.borderFlash) return '#00B383';
    if (this.focused) return '#1A3066';
    return '#E9EEFB';
  }

  private async performSave(): Promise<void> {
    this.debounceTimer = null;
    if (!this.projectId) return;

    let project;
    try {
      project = await firstValueFrom(this.apiResponseProjects.getById(this.projectId));
    } catch (err: any) {
      if (err?.status === 404) this.projectNotFound.emit();
      return;
    }

    if (!project.is_active) {
      this.projectClosed.emit();
      return;
    }

    try {
      await firstValueFrom(
        this.apiResponseProjects.updateResponseProject({
          id: this.projectId,
          user_instructions: this.value,
        })
      );
      this.saveStatus = 'saved';
      this.lastSaved = new Date();
      this.borderFlash = true;
      setTimeout(() => {
        this.borderFlash = false;
      }, 1500);
      this.startRelativeTimeUpdates();
      this.confirmedTimer = setTimeout(() => {
        this.saveStatus = 'confirmed';
      }, 2000);
    } catch (err: any) {
      if (err?.status === 404) {
        this.projectNotFound.emit();
      } else {
        this.saveStatus = 'idle';
      }
    }
  }

  private startRelativeTimeUpdates(): void {
    if (this.relativeTimeInterval) {
      clearInterval(this.relativeTimeInterval);
    }
    this.updateRelativeTime();
    this.relativeTimeInterval = setInterval(() => this.updateRelativeTime(), 5000);
  }

  private updateRelativeTime(): void {
    if (!this.lastSaved) return;

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - this.lastSaved.getTime()) / 1000);

    if (diffInSeconds < 10) {
      this.relativeTimeKey = 'tenders.responses.instructions.relative-seconds-ago';
      this.relativeTimeParams = {};
    } else if (diffInSeconds < 60) {
      this.relativeTimeKey = 'tenders.responses.instructions.relative-x-seconds';
      this.relativeTimeParams = { count: diffInSeconds };
    } else if (diffInSeconds < 120) {
      this.relativeTimeKey = 'tenders.responses.instructions.relative-1-minute';
      this.relativeTimeParams = {};
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      this.relativeTimeKey = 'tenders.responses.instructions.relative-x-minutes';
      this.relativeTimeParams = { count: minutes };
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      this.relativeTimeKey =
        hours === 1 ? 'tenders.responses.instructions.relative-1-hour' : 'tenders.responses.instructions.relative-x-hours';
      this.relativeTimeParams = hours === 1 ? {} : { count: hours };
    }
  }

  private clearTimers(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.confirmedTimer) {
      clearTimeout(this.confirmedTimer);
      this.confirmedTimer = null;
    }
    if (this.relativeTimeInterval) {
      clearInterval(this.relativeTimeInterval);
      this.relativeTimeInterval = null;
    }
  }

  get statusSaved(): boolean {
    return this.saveStatus === 'saved';
  }

  get statusConfirmed(): boolean {
    return (this.saveStatus === 'confirmed' || this.saveStatus === 'idle') && this.lastSaved !== null;
  }
}
